import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { GridItem, TextField } from 'seagull';
import { getOptionalAgreementNameValidation, getOptionalAgreementNameValidator } from '~/business-logic/validators';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { DefaultValuePopover } from '../TemplateVariables/DefaultValuePopover/DefaultValuePopover';
import { useTextFieldVariableIntegration } from '../TemplateVariables/hooks/useTextFieldVariableIntegration';
import { useTemplateVariables } from '../TemplateVariables/TemplateVariablesContext';
import { VariableFieldAdornment } from '../TemplateVariables/VariableFieldAdornment/VariableFieldAdornment';
import { VariablePickerPopover } from '../TemplateVariables/VariablePickerPopover/VariablePickerPopover';
import { VariablePreviewLine } from '../TemplateVariables/VariablePreviewLine/VariablePreviewLine';
const AGREEMENT_NAME_EXCLUDE_LABELS = [
    'Agreement Name'
];
export function AgreementName(param) {
    let { agreementTitle, showWarning, setAgreementTitle, setAgreementTitleValid, label, disabled, testId, allowEmpty = false, fullWidth } = param;
    const { t } = useLocaleKeys();
    const { t: agreementNameTexts } = useLocaleKeys('components.agreementStudio.agreementName');
    const { control, formState, watch, trigger, setValue } = useForm({
        mode: 'all',
        defaultValues: {
            agreementTitle
        }
    });
    const inputRef = useRef(null);
    const watchedTitle = watch('agreementTitle');
    const { showVariableErrors, unresolvedCustomVariables } = useTemplateVariables();
    const hasUnresolvedTags = showVariableErrors && unresolvedCustomVariables.some((label)=>watchedTitle.includes("{{".concat(label, "}}")));
    const setAgreementTitleField = useCallback((newValue)=>setValue('agreementTitle', newValue), [
        setValue
    ]);
    const { enabled, inputProps, adornmentRef, adornmentStyle, fieldAdornmentProps, resolvedValues, pickerPopoverProps, defaultValuePopoverProps } = useTextFieldVariableIntegration(inputRef, setAgreementTitleField, {
        disabled,
        excludeLabels: AGREEMENT_NAME_EXCLUDE_LABELS
    });
    useEffect(()=>{
        return watch((data)=>{
            var _data_agreementTitle;
            setAgreementTitle((_data_agreementTitle = data.agreementTitle) !== null && _data_agreementTitle !== void 0 ? _data_agreementTitle : '');
        }).unsubscribe;
    }, [
        setAgreementTitle,
        watch
    ]);
    useEffect(()=>{
        setAgreementTitleValid(formState.isValid);
    }, [
        formState,
        setAgreementTitleValid
    ]);
    useEffect(()=>{
        if (showWarning) trigger('agreementTitle');
    }, [
        showWarning,
        trigger
    ]);
    useOnMount(()=>{
        trigger('agreementTitle');
    });
    const agreementNameValidation = getOptionalAgreementNameValidation(allowEmpty);
    const agreementNameValidator = getOptionalAgreementNameValidator(allowEmpty);
    const fieldElement = /*#__PURE__*/ _jsx(Controller, {
        control: control,
        name: "agreementTitle",
        rules: agreementNameValidation,
        render: (param)=>{
            let { field: controllerField, fieldState: { isTouched, error } } = param;
            const { ref: rhfRef } = controllerField, restField = _object_without_properties(controllerField, [
                "ref"
            ]);
            return /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread(_object_spread_props(_object_spread({
                collapseHelperText: true,
                mobile: {
                    renderAsCard: true
                }
            }, restField), {
                ref: undefined,
                inputRef: (el)=>{
                    inputRef.current = el;
                    rhfRef(el);
                }
            }), agreementNameValidation.dataAttr || {}), {
                label: label,
                disabled: disabled,
                testId: testId,
                bi: {
                    name: 'agreement_name'
                },
                placeholder: agreementNameTexts.placeholder(),
                error: (showWarning || isTouched) && !!error || hasUnresolvedTags,
                helperText: hasUnresolvedTags ? t.components.templateVariables.section.fieldHelperText() : (showWarning || isTouched) && (error === null || error === void 0 ? void 0 : error.message),
                validateInBottomSheet: (value)=>{
                    const result = agreementNameValidator(value);
                    return {
                        error: result !== true,
                        helperText: typeof result === 'string' ? result : undefined
                    };
                },
                inputProps: inputProps,
                InputProps: enabled ? {
                    endAdornment: /*#__PURE__*/ _jsx("span", {
                        ref: adornmentRef,
                        style: adornmentStyle,
                        children: /*#__PURE__*/ _jsx(VariableFieldAdornment, _object_spread({}, fieldAdornmentProps))
                    })
                } : undefined
            }));
        }
    });
    return fullWidth ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(GridItem, {
                span: 12,
                children: [
                    fieldElement,
                    enabled && /*#__PURE__*/ _jsx(VariablePreviewLine, {
                        text: watchedTitle,
                        resolvedValues: resolvedValues
                    })
                ]
            }),
            enabled && /*#__PURE__*/ _jsx(VariablePickerPopover, _object_spread({}, pickerPopoverProps)),
            enabled && /*#__PURE__*/ _jsx(DefaultValuePopover, _object_spread({}, defaultValuePopoverProps))
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(GridItem, {
                span: 9,
                children: [
                    fieldElement,
                    enabled && /*#__PURE__*/ _jsx(VariablePreviewLine, {
                        text: watchedTitle,
                        resolvedValues: resolvedValues
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(GridItem, {
                span: 3
            }),
            enabled && /*#__PURE__*/ _jsx(VariablePickerPopover, _object_spread({}, pickerPopoverProps)),
            enabled && /*#__PURE__*/ _jsx(DefaultValuePopover, _object_spread({}, defaultValuePopoverProps))
        ]
    });
}
